/** It reads `user.id` and takes no person parameter, so it cannot serve anyone else. */

import { useQuery } from '@tanstack/react-query'
import { CalendarDays, Clock, Monitor, Network, type LucideIcon } from 'lucide-react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { api } from '../lib/api'
import type { Personal } from '../lib/dashboard'
import { greetingKey, nextShift, quickLinkVisible, reservedStatusRows, timeLine } from '../lib/homeWall'
import { useDays } from '../lib/time'
import { can, type User } from '../lib/types'
import type { OwnSchedule } from '../pages/roster/MySchedule'
import { addDays, today as todayIso } from '../pages/time/dates'
import { leaveHref } from '../pages/time/leaveLink'
import { formatDate } from '../lib/dateFormat'

function useOwnRoster(from: string, to: string) {
  return useQuery({
    queryKey: ['roster', 'me', from, to],
    queryFn: async () =>
      (await api.get<OwnSchedule>('/api/roster/me', { params: { from, to } })).data,
    staleTime: 5 * 60_000,
    retry: false,
  })
}

// Labels are literal `t()` calls (not keys passed as data) so i18n/keys.test.ts can see each key from
// its call site.
function quickLinks(t: TFunction): { to: string; label: string; icon: LucideIcon }[] {
  return [
    { to: '/time', label: t('homewall.linkLogTime'), icon: Clock },
    { to: leaveHref('own', ''), label: t('homewall.linkLeave'), icon: CalendarDays },
    { to: '/hot-desk', label: t('homewall.linkDesk'), icon: Monitor },
    { to: '/organization?tab=orgchart', label: t('homewall.linkOrgChart'), icon: Network },
  ]
}

function greeting(t: TFunction, hour: number, name: string): string {
  switch (greetingKey(hour)) {
    case 'homewall.morning':
      return t('homewall.morning', { name })
    case 'homewall.afternoon':
      return t('homewall.afternoon', { name })
    case 'homewall.evening':
      return t('homewall.evening', { name })
  }
}

function StatusLine({ icon: Icon, children }: { icon: LucideIcon; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />
      <span className="min-w-0">{children}</span>
    </li>
  )
}

export function HomeBasics({
  user,
  personal,
  personalLoading,
}: {
  user: User | undefined
  personal: Personal | undefined
  personalLoading: boolean
}) {
  const { t, i18n } = useTranslation()
  const today = todayIso()
  const days = useDays(user?.id, today, today, can(user, 'self.log'))
  const roster = useOwnRoster(today, addDays(today, 30))

  const own = (days.data ?? []).find((d) => d.employee_id === user?.id && d.kind === 'Actual')
  const off = personal?.next_day_off
  const offToday = off && off.days_away === 0 ? off : null
  const desk = personal?.desk_week.find((d) => d.day === today)
  const shift = roster.data?.person ? nextShift(roster.data, today) : null
  const date = (iso: string) =>
    formatDate(new Date(`${iso}T00:00:00`), i18n.language, { weekday: 'short', month: 'short', day: 'numeric' })

  const line = timeLine({
    offToday: !!offToday,
    mayLog: can(user, 'self.log'),
    loading: days.isLoading,
    logged: !!own,
  })
  const firstName = user?.display_name.split(/\s+/)[0] ?? ''
  const reserved = reservedStatusRows({
    personalLoading,
    rosterLoading: roster.isLoading,
    onRoster: !!user?.roster_access?.role,
  })

  return (
    <section className="grid gap-3 rounded-2xl border border-slate-200 bg-gradient-to-br from-sky-50 to-white p-4 dark:border-slate-700 dark:from-slate-800 dark:to-slate-900 h-full sm:p-5 @2xl:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0 space-y-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100 sm:text-2xl">
            {greeting(t, new Date().getHours(), firstName)}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {formatDate(new Date(), i18n.language, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <ul className="space-y-1.5" aria-label={t('homewall.todayTitle')}>
          {line === 'off' && offToday ? (
            <StatusLine icon={CalendarDays}>
              {offToday.kind === 'holiday'
                ? t('homewall.holidayToday', { label: offToday.label })
                : t('homewall.onLeaveToday', { label: offToday.label })}
            </StatusLine>
          ) : line === 'hidden' ? null : (
            <StatusLine icon={Clock}>
              {line === 'loading'
                ? t('action.loading')
                : own
                  ? t('homewall.timeLogged', { status: t(`time.status.${own.status}`, own.status) })
                  : t('homewall.timeNotLogged')}
            </StatusLine>
          )}
          {shift && (
            <StatusLine icon={CalendarDays}>
              {shift.isToday
                ? t('homewall.shiftToday', { code: shift.code })
                : t('homewall.shiftNext', { date: date(shift.day), code: shift.code })}
            </StatusLine>
          )}
          {roster.data?.person && !shift && <StatusLine icon={CalendarDays}>{t('homewall.shiftNone')}</StatusLine>}
          {desk && (
            <StatusLine icon={Monitor}>{t('homewall.deskToday', { desk: desk.desk })}</StatusLine>
          )}
          {off && !offToday && (
            <StatusLine icon={CalendarDays}>
              {t('homewall.nextDayOff', { date: date(off.day), label: off.label })}
            </StatusLine>
          )}
          {Array.from({ length: reserved }, (_, i) => (
            <li key={`reserved-${i}`} aria-hidden className="flex h-5 items-center">
              <span className="h-3 w-40 max-w-full animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
            </li>
          ))}
        </ul>
      </div>
      <nav aria-label={t('homewall.quickLinks')} className="grid grid-cols-2 gap-2 self-start @lg:grid-cols-4 @2xl:w-80 @2xl:grid-cols-2">
        {quickLinks(t)
          .filter(({ to }) => quickLinkVisible(to, (ability) => can(user, ability)))
          .map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 hover:border-sky-300 hover:bg-sky-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-sky-700 dark:hover:bg-slate-700/60"
          >
            <Icon className="h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />
            <span className="min-w-0 leading-tight">{label}</span>
          </Link>
        ))}
      </nav>
    </section>
  )
}
